import { useRef, type ReactNode, type RefObject } from "react";
import type { Caixa } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { posicao } from "./GradeDaPlanta";

type Props = {
  caixa: Caixa;
  colunas: number;
  linhas: number;
  grade: RefObject<HTMLDivElement | null>;
  selecionada: boolean;
  rotulo: string;
  onSelecionar: () => void;
  onPosicionar: (c: Caixa) => void;
  onRemover: () => void;
  children: ReactNode;
};

/**
 * Peça da planta no editor: arrastar move, a alça do canto redimensiona, tudo em células inteiras.
 * Teclado: setas movem, Shift + setas redimensionam, Delete remove.
 */
export function PecaEditavel({ caixa, colunas, linhas, grade, selecionada, rotulo, onSelecionar, onPosicionar, onRemover, children }: Props) {
  const inicio = useRef<{ px: number; py: number; caixa: Caixa; modo: "mover" | "redimensionar" } | null>(null);

  const comecar = (e: React.PointerEvent, modo: "mover" | "redimensionar") => {
    e.stopPropagation();
    onSelecionar();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    inicio.current = { px: e.clientX, py: e.clientY, caixa, modo };
  };
  const arrastar = (e: React.PointerEvent) => {
    const i = inicio.current;
    const r = grade.current?.getBoundingClientRect();
    if (!i || !r) return;
    const dx = Math.round(((e.clientX - i.px) / r.width) * colunas);
    const dy = Math.round(((e.clientY - i.py) / r.height) * linhas);
    onPosicionar(i.modo === "mover" ? { ...i.caixa, x: i.caixa.x + dx, y: i.caixa.y + dy } : { ...i.caixa, w: i.caixa.w + dx, h: i.caixa.h + dy });
  };
  const soltar = () => {
    inicio.current = null;
  };

  const teclado = (e: React.KeyboardEvent) => {
    const d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
    if (d) {
      e.preventDefault();
      onPosicionar(e.shiftKey ? { ...caixa, w: caixa.w + d[0]!, h: caixa.h + d[1]! } : { ...caixa, x: caixa.x + d[0]!, y: caixa.y + d[1]! });
    } else if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      onRemover();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${rotulo}: arraste para mover`}
      aria-pressed={selecionada}
      onPointerDown={(e) => comecar(e, "mover")}
      onPointerMove={arrastar}
      onPointerUp={soltar}
      onPointerCancel={soltar}
      onFocus={onSelecionar}
      onKeyDown={teclado}
      className="absolute touch-none p-0.5 focus:outline-none"
      style={posicao(caixa, colunas, linhas)}
    >
      <div className={cn("relative h-full w-full cursor-move rounded-lg", selecionada && "ring-2 ring-brand-500 ring-offset-1 dark:ring-offset-background")}>
        {children}
        <span
          aria-hidden
          onPointerDown={(e) => comecar(e, "redimensionar")}
          onPointerMove={arrastar}
          onPointerUp={soltar}
          onPointerCancel={soltar}
          className="absolute -bottom-1 -right-1 h-3.5 w-3.5 cursor-nwse-resize rounded-sm border-2 border-white bg-brand-600 shadow-sm dark:border-background"
        />
      </div>
    </div>
  );
}
